import React from 'react';
import { FiImage, FiPlay, FiMusic, FiVideo } from 'react-icons/fi';
import { Link } from '@tanstack/react-router';
import { type MediaItem } from '../../api/media';

interface MediaTabProps {
  media: MediaItem[];
  isLoading?: boolean;
}

function isVideoUrl(url: string): boolean {
  return /\.(mp4|mov|webm|avi|mkv|m4v)(\?|$)/i.test(url);
}

function videoUrlToThumbnail(url: string): string {
  return url.replace(/\.(mp4|mov|avi|webm|m4v|mkv)(\?.*)?$/i, '.png');
}

// For the grid thumbnail: always swap video extension to .png for Cloudinary poster
function getGridThumbnail(item: MediaItem): { url: string; renderAsImage: boolean } {
  const urls =
    Array.isArray(item.media_urls) && item.media_urls.length > 0
      ? item.media_urls
      : item.file_url
        ? [item.file_url]
        : [];

  if (!urls.length) return { url: '', renderAsImage: true };

  const raw = urls[0];
  // Always swap video extension to .png so Cloudinary serves a poster image
  const url = isVideoUrl(raw) ? videoUrlToThumbnail(raw) : raw;
  return { url, renderAsImage: true };
}

export const MediaTab: React.FC<MediaTabProps> = ({ media, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl overflow-hidden bg-zinc-900 animate-pulse aspect-4/5"
          />
        ))}
      </div>
    );
  }

  if (!media || media.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-600">
          <FiImage size={28} />
        </div>
        <div>
          <p className="text-white text-lg font-semibold">No Media Yet</p>
          <p className="text-zinc-500 text-sm mt-1">You have not created any media yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
      {media.map((item) => {
        const isAudio = item.media_type === 'audio';
        const isVideoItem = item.media_type === 'video';
        const { url: thumbnail } = getGridThumbnail(item);
        const title = item.title || '';

        return (
          <Link
            key={item.id}
            to="/media/$mediaId"
            params={{ mediaId: String(item.id) }}
            className="group relative shrink-0 aspect-4/5 overflow-hidden rounded-xl cursor-pointer bg-zinc-900 border border-white/5 block"
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={title || 'Media'}
                className="w-full h-full object-cover transform group-hover:scale-105 transition-all duration-700 ease-out"
              />
            ) : isAudio ? (
              <div className="w-full h-full flex items-center justify-center bg-zinc-900/50">
                <FiMusic
                  className="text-blue-500/20 group-hover:text-blue-500/40 transition-colors"
                  size={48}
                />
              </div>
            ) : (
              <div className="w-full h-full bg-zinc-900" />
            )}

            {/* Musicade logo mark */}
            <div className="absolute top-3 left-3 z-10 opacity-60">
              <div className="w-5 h-5 flex flex-col justify-between items-center rotate-180">
                <div className="w-1 h-2.5 bg-white rounded-full translate-y-1" />
                <div className="flex gap-1">
                  <div className="w-1 h-1.5 bg-white rounded-full" />
                  <div className="w-1 h-1.5 bg-white rounded-full" />
                </div>
              </div>
            </div>

            {/* Video badge */}
            {isVideoItem && (
              <div className="absolute top-3 right-3 z-10 w-6 h-6 rounded-full bg-black/50 backdrop-blur-sm border border-white/15 flex items-center justify-center">
                <FiVideo size={11} className="text-white" />
              </div>
            )}

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-linear-to-t from-black via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

            {/* Title */}
            <div className="absolute inset-x-0 bottom-0 p-3 flex flex-col items-center text-center">
              {title && (
                <h3 className="text-[12px] font-bold text-white leading-tight uppercase tracking-tight line-clamp-3 group-hover:text-blue-400 transition-colors">
                  {title}
                </h3>
              )}
            </div>

            {/* Play button on hover */}
            <div className="absolute bottom-3 right-3 opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
              <div className="w-7 h-7 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:bg-blue-600 transition-colors">
                {isAudio ? <FiMusic size={12} /> : <FiPlay size={12} className="fill-current" />}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};
